import { Params, generatePath, matchPath } from 'react-router-dom';
import { EXTERNAL_URLS, LEGACY_PATHS, PATHS, Route } from '../../routing/routes';
import { QueryParams } from '../enums/QueryParams';
import { Severity } from '../enums/Severity';
import { isKeyOf } from './guards';

type GeneratePathOptions = {
  keepQueryParams?: boolean;
};

type ProjectDashboardUrlOptions = {
  displaySeverity?: Severity;
  focusToIssueNumber?: number;
};

export class UrlHelper {
  static generatePath(path: string, params: Params = {}, options: GeneratePathOptions = {}) {
    const queryParams = new URLSearchParams(options.keepQueryParams ? window.location.search : '');

    /*
     With React 18, react-router won't replace query params as part of the string template,
     meaning that all the template urls that were using query params as such won't work anymore.

     Eg., generatePath("/project/:projectId/slots?slotId=:slotId", {projectId: 1, slotId: 2}) will
     generate following url: "/project/1/slots?slotId=:slotId". Since ":slotId" is not part of the
     url template (it is part of the query param), we need to manually handle this templating.
   */

    const [templatePath, rawQueryParams = ''] = path.split('?');
    // If we have query params, iterate through them and replace them with passed in values.
    if (rawQueryParams) {
      // Parse the raw query params to get all the key values
      const additionalQueryParams = new URLSearchParams(rawQueryParams);
      for (const [key, value] of additionalQueryParams.entries()) {
        queryParams.set(key, value);
      }
    }

    /*
     For each key, check if there is a value in params.

     eg, for the "/project/:projectId/slots?slotIdentifier=:slotId", we would currently have
     URLSearchParams("slotIdentifier=:slotId"), which is equivalent to this:

       {
        "slotIdentifier": ":slotId"
       }

     We now iterate through all the keys and check if their value is present in the params.

     In this case, for key "slotIdentifier" we extract ":slotId", clean that up to get `slotId` and check if there is a
     param for that key.

     If there is, we replace that value to get to the final state of the object (implying that we have
     a param "slotId" with value "2"):

       {
        "slotIdentifier": "2"
       }

     That is finally passed to the UrlHelper to create the url.
     */
    for (const key of queryParams.keys()) {
      // Strip the `:` from the value in query params.
      const valueKey = queryParams.get(key)?.replace(/^:/, '') || '';

      if (isKeyOf(valueKey, params)) {
        queryParams.set(key, params[valueKey] as string);
      }
    }

    const url = generatePath(templatePath, { ...params });

    // URLSearchParams.size has inconsistent support, so we use this to check if empty.
    if ([...queryParams.keys()].length === 0) {
      return url;
    }

    return `${url}?${queryParams.toString()}`;
  }

  static generatePaths(routes: Route[], params: Params): Route[] {
    return routes.map((route) => ({
      ...route,
      path: generatePath(route.path, params),
    }));
  }

  static getFlatPaths(routes: Route[]): string[] {
    return routes.map((r) => [r.path, ...(r.nestedPaths || [])]).flat();
  }

  static getQueryParam(key: string, defaultValue: string | null = null): string | null {
    const urlSearchParams = new URLSearchParams(window.location.search);

    if (!urlSearchParams.has(key)) {
      return defaultValue;
    }

    return urlSearchParams.get(key);
  }

  static clearQueryParams() {
    window.history.replaceState(window.history.state, '', window.location.pathname);
  }

  static isCurrentPath(path: string): boolean {
    return UrlHelper.matchPath(path);
  }

  static matchPath(pattern: string, url: string = window.location.href): boolean {
    // Strip the `url` of any query and hash parameters and isolate the pathname
    const { pathname } = new URL(url, window.location.href);
    // Match `pattern` against the `pathname`
    const match = matchPath({ path: pattern, end: true }, pathname);
    if (!match) return false;
    return Object.entries(match.params).every(([key, value]) => {
      if (key.endsWith('Id')) return /^\d+$/.test(value || '');
      if (key.endsWith('Name')) return /^[a-zA-Z-]+$/.test(value || '');
      return true;
    });
  }

  static generateProjectDashboardUrl(projectId: number, options: ProjectDashboardUrlOptions = {}): string {
    const baseUrl = UrlHelper.generatePath(PATHS.PROJECT_DASHBOARD, { projectId: projectId.toString() });

    const urlQueryParams = new URLSearchParams();
    const { displaySeverity, focusToIssueNumber } = options;
    if (displaySeverity !== undefined) {
      urlQueryParams.set(QueryParams.SEVERITY, displaySeverity.toString());
    }

    if (focusToIssueNumber !== undefined) {
      urlQueryParams.set(QueryParams.ISSUE_NUMBER, focusToIssueNumber.toString());
    }

    return `${baseUrl}?${urlQueryParams.toString()}`;
  }

  static wrapExternalDocsUrl(path: string): string {
    return `${EXTERNAL_URLS.DOCS_BASE}${path}`;
  }

  static wrapKnowledgeBaseUrl(path: string): string {
    if (!path.startsWith(LEGACY_PATHS.KNOWLEDGE_BASE)) {
      // URL is not a knowledge base, return as is
      return path;
    }
    // Tiny hack to add the trailing slash required by mkdocs only if it's not there yet
    return UrlHelper.wrapExternalDocsUrl(`${path}/`.replace('//', '/'));
  }

  /**
   * Generates a URL to logout the user from the Flask server.
   * Requires a `path` to which the user will be redirected to.
   */
  static generateLogoutUrl() {
    const isIgnoredPath = [PATHS.LOGIN_PAGE, PATHS.LOGOUT_PAGE, PATHS.SESSION_PICKUP_PAGE].find((p) => UrlHelper.isCurrentPath(p));
    if (isIgnoredPath) {
      return `${PATHS.LOGOUT_PAGE}?next=${PATHS.ROOT}`;
    }
    return `${PATHS.LOGOUT_PAGE}?next=${window.location.pathname}`;
  }

  static isLogoutUrl(url: string | null) {
    return url?.startsWith(PATHS.LOGOUT_PAGE);
  }
}
